import type { SVGProps } from "react";

/**
 * Inline stroke icons for the shared components (and available to apps via
 * `Icons.*`). 24-box, 2px stroke, currentColor — size them with a class.
 */
type P = Omit<SVGProps<SVGSVGElement>, "viewBox" | "children">;
const base: P = { fill: "none", "aria-hidden": true, focusable: false };

function Stroke({ d, ...props }: P & { d: string }) {
	return (
		<svg viewBox="0 0 24 24" {...base} {...props}>
			<path d={d} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	);
}

export const ChevronLeft = (p: P) => <Stroke {...p} d="M15 6l-6 6 6 6" />;
export const ChevronRight = (p: P) => <Stroke {...p} d="M9 6l6 6-6 6" />;
export const ChevronDown = (p: P) => <Stroke {...p} d="M6 9l6 6 6-6" />;
export const ChevronsLeft = (p: P) => <Stroke {...p} d="M11 6l-6 6 6 6M18 6l-6 6 6 6" />;
export const ChevronsRight = (p: P) => <Stroke {...p} d="M6 6l6 6-6 6M13 6l6 6-6 6" />;
export const Close = (p: P) => <Stroke {...p} d="M6 6l12 12M18 6L6 18" />;
export const Check = (p: P) => <Stroke {...p} d="M5 12l5 5L20 7" />;
export const ArrowUp = (p: P) => <Stroke {...p} d="M12 19V5M5 12l7-7 7 7" />;
export const ArrowDown = (p: P) => <Stroke {...p} d="M12 5v14M19 12l-7 7-7-7" />;
/** Unsorted hint: two small chevrons, faint. */
export const ArrowsUpDown = (p: P) => <Stroke {...p} d="M8 9l4-4 4 4M8 15l4 4 4-4" />;
export const InboxEmpty = (p: P) => (
	<Stroke
		{...p}
		d="M4 13h4l2 3h4l2-3h4M4 13l1.5-7.5A1 1 0 0 1 6.5 5h11a1 1 0 0 1 1 .8L20 13M4 13v5a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-5"
	/>
);
export const Alert = (p: P) => (
	<Stroke {...p} d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
);
export const Eye = (p: P) => (
	<Stroke {...p} d="M2.5 12s3.5-7 9.5-7 9.5 7 9.5 7-3.5 7-9.5 7-9.5-7-9.5-7zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
);
export const Pencil = (p: P) => <Stroke {...p} d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />;
export const Trash = (p: P) => <Stroke {...p} d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14M10 11v6M14 11v6" />;
export const Search = (p: P) => <Stroke {...p} d="m21 21-4.3-4.3M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14z" />;
export const Filter = (p: P) => <Stroke {...p} d="M3 5h18l-7 8v6l-4 2v-8L3 5z" />;
export const RotateCcw = (p: P) => <Stroke {...p} d="M3 12a9 9 0 1 0 3-6.7M3 4v5h5" />;
export const Unlock = (p: P) => <Stroke {...p} d="M7 11V7a5 5 0 0 1 9.9-1M5 11h14v10H5z" />;
export const Ban = (p: P) => <Stroke {...p} d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM5.6 5.6l12.8 12.8" />;
export const Plus = (p: P) => <Stroke {...p} d="M12 5v14M5 12h14" />;
export const Columns = (p: P) => <Stroke {...p} d="M4 5h16v14H4zM10 5v14M16 5v14" />;
export const Key = (p: P) => (
	<Stroke {...p} d="M15.5 3a5.5 5.5 0 0 0-5.3 7L3 17.2V21h3.8l1.2-1.2V18h1.8v-1.8h1.8l1.4-1.4A5.5 5.5 0 1 0 15.5 3zm1.5 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z" />
);
export const Copy = (p: P) => (
	<Stroke {...p} d="M10 8h9a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1zM5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" />
);
